import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { fetcher, getFetcher, PRODUCT_TITLE } from 'App';
// import queryString from 'querystring';
import useSWR from 'swr';
import moment from 'moment';
import autoTable from 'jspdf-autotable';
import jsPDF from 'jspdf';
import { comma, headCase, masking } from 'utils/lib';
import { StyledFullWidth, StyledStatus } from './styles';
import { IHistoryDetailTodos, Todo } from '../HistoryDetail/types';
import { StyledBodyRow } from '../History/style';
import { Pagination, Worklist } from '@components/organisms';
import TodosFilter from '@components/molecules/History/TodosFilter';
import { ErrorCode, ErrorMessage } from '@variables/error';

import NanumGothic from '@assets/fonts/NanumGothic/NanumGothic-Regular.ttf';
import { debounce } from 'lodash';
import he from 'he';
import qs from 'qs';

interface Props {
  licenseId: string;
  accessToken: string;
  defaultFromDate: string;
  maxDate?: string;
}

const HistoryDetailTodos = ({ accessToken, licenseId, defaultFromDate, maxDate }: Props) => {
  const [downloadProgress, setDownloadProgress] = useState(false);
  const [conditionsValue, setConditionsValue] = useState<any>({
    searchValue: '',
    scanFromDate: null,
    scanToDate: null,
    analysisFromDate: null,
    analysisToDate: null,
    countPerPage: 15,
    currentPage: 1,
    resultList: [],
  });

  const url = useMemo(() => {
    const endpoint = `api/license/${licenseId}/usage-history`;
    const query = qs.stringify(conditionsValue);

    // console.log(conditionsValue, 'conditionsValue');
    // console.log(query, 'query');
    // console.log(qs.stringify(conditionsValue), 'qs.stringify(conditionsValue)');

    return endpoint + '?' + query;
  }, [licenseId, conditionsValue]);

  const { data, error } = useSWR<IHistoryDetailTodos>(accessToken ? [{ url, accessToken }] : null);

  /**
   * PDF 저장
   */
  const savePDFHandler = debounce(async () => {
    const allData = await getAllData();

    const data = allData.map(
      ({ no, patientId, patientName, scanDatetime, analysisDatetime, registerId, result, remarks }) => {
        const MASK_patientId = masking(patientId, 2);
        const MASK_patientName = masking(patientName, 1, true);

        return [no || 0, MASK_patientId, MASK_patientName, scanDatetime, analysisDatetime, registerId, result, remarks];
      },
    );

    const doc = new jsPDF({
      orientation: 'l',
      unit: 'mm',
      format: 'a4',
      encryption: {
        userPermissions: ['print'],
      },
    });

    doc.addFont(NanumGothic, 'NanumGothic', 'normal');
    doc.setFont('NanumGothic');

    autoTable(doc, {
      headStyles: {
        halign: 'center',
        minCellHeight: 8,
        fillColor: '#7fe200',
      },
      bodyStyles: {
        halign: 'center',
      },
      margin: { top: 4, bottom: 4, left: 4, right: 4 },
      styles: {
        font: 'NanumGothic',
        fontStyle: 'normal',
      },
      head: [['NO', 'Patient ID', 'Name', 'Scan date', 'Analysis date', 'Register', 'Result', 'Remarks']],
      body: [...data],
    });

    let { analysisEndDate, analysisFromDate } = conditionsValue;

    const fromDate = analysisFromDate || defaultFromDate || undefined;
    const toDate = analysisEndDate || (allData[0] && allData[0].analysisDatetime) || undefined;

    analysisFromDate = moment(fromDate).format('YYYYMMDD');
    analysisEndDate = moment(toDate).format('YYYYMMDD');

    const downloadDateTime = moment().format('YYYYMMDD-HHmm');
    const downloadName = `${analysisFromDate}-${analysisEndDate}_라이선스사용내역_${PRODUCT_TITLE}_${downloadDateTime}.pdf`;

    doc.save(downloadName);

    setDownloadProgress(false);
  }, 500);

  /**
   *
   */
  const getAllData = useCallback(async () => {
    let totalData: Todo[] = [];

    setDownloadProgress(true);

    if (!data) return totalData;

    // scope 제한사용
    let _totalCount = 0;
    const _conditionValues = { ...conditionsValue, resultList: ['COMPLETE'] };
    const baseURL = `api/license/${licenseId}/usage-history`;

    //////////
    // PDF 생성에 필요한  total count 정보 호출
    const query = qs.stringify({ resultList: ['COMPLETE'] });

    await getFetcher({ method: 'GET', accessToken: accessToken })(baseURL + '?' + query).then((res) => {
      const totalCount = res.result.condition.totalCount;
      _totalCount = totalCount;
    });
    /////////

    const countPerPage = 1000;
    const totalPageCount = Math.ceil(_totalCount / countPerPage);

    for (let i = 0; i < totalPageCount; i++) {
      // 페이지네이션
      _conditionValues.currentPage = i + 1;

      const _query = qs.stringify({ ..._conditionValues, countPerPage: countPerPage });
      const url = baseURL + '?' + _query;

      // 데이터 요청
      const data = await fetcher<IHistoryDetailTodos>({ url, accessToken: accessToken });

      if (data.code === '200') {
        const todos = data.result.results;
        totalData = totalData.concat(todos);
      }
    }

    // PDF에 나갈 넘버링
    const totalCount = totalData.length;

    return totalData.map((item, i) => {
      return {
        ...item,
        no: totalCount - i,
        analysisDatetime: !item.analysisDatetime ? '-' : moment(item.analysisDatetime).format('YYYY-MM-DD'),
        scanDatetime: moment(item.scanDatetime).format('YYYY-MM-DD'),
        registerId: (item.registerId || 'Empty').replace('&#40;', '(').replace('&#41;', ')'),
        result: headCase(item.result),
        remarks: ErrorMessage[ErrorCode[item.remarks]] || '-',
      };
    });
  }, [data, conditionsValue, licenseId, accessToken]);

  /**
   *
   */
  const conditions = useMemo(() => {
    if (!data) return conditionsValue;
    return data.result.condition;
  }, [data, conditionsValue]);

  /**
   * Data 재배치
   */
  const dataArray = useMemo(() => {
    if (!data || data.result.results.length === 0) return [];

    const { totalCount } = data.result.condition;

    const startNumber = totalCount - (conditionsValue.currentPage - 1) * conditionsValue.countPerPage;

    return data.result.results.map((item, i) => {
      return {
        ...item,
        no: startNumber - i,
        analysisDatetime: !item.analysisDatetime ? '-' : moment(item.analysisDatetime).format('YYYY-MM-DD'),
        scanDatetime: moment(item.scanDatetime).format('YYYY-MM-DD'),
        registerId: he.decode(item.registerId) || '',
        patientName: he.decode(item.patientName) || '',
        result: headCase(item.result),
        remarks: ErrorMessage[ErrorCode[item.remarks]] || '-',
      };
    });
  }, [data, conditionsValue]);

  if (error) {
    return <div>failed to load</div>;
  }

  return (
    <div>
      <TodosFilter
        defaultToDate={maxDate}
        defaultFromDate={defaultFromDate}
        downloadProgress={downloadProgress}
        setSearchConditions={setConditionsValue}
        onPDFDownload={savePDFHandler}
      />

      <StyledFullWidth>
        <div className="total-count">Total {comma(data?.result.condition.totalCount)}</div>
        <Worklist
          headers={[
            { label: 'NO', data_label: 'no' },
            { label: 'Patient ID', data_label: 'patientId' },
            { label: 'Name', data_label: 'patientName' },
            { label: 'Scan date', data_label: 'scanDatetime' },
            { label: 'Analysis date', data_label: 'analysisDatetime' },
            { label: 'Register', data_label: 'registerId' },
            { label: 'Result', data_label: 'result' },
            { label: 'Remarks', data_label: 'remarks' },
          ]}
          data={{ dataArray }}
          customColumn={{
            result: (row) => {
              return <StyledStatus className={row.result.toLowerCase()}>{row.result}</StyledStatus>;
            },
          }}
          styles={{ tbody: StyledBodyRow }}
        />

        <Pagination conditions={conditions ?? conditionsValue} setConditions={setConditionsValue} />
      </StyledFullWidth>
    </div>
  );
};

export default HistoryDetailTodos;
